/**
 * withOperationFeedback — sjednocené UX pro dlouhé MCP operace.
 *
 * Threshold-based loading toast + výsledek. Filip 2026-07-22:
 * „ACM když provádí příkaz přes UI by mělo ukazovat co dělá a že se něco
 *  děje pokaždé když to překročí nějaký čas a výsledek."
 *
 * Chování:
 *   - Volání < 800ms → ŽÁDNÝ platform toast, ať už success nebo failure.
 *     Komponenty si onError/onSuccess řeší samy (existing pattern — mnoho
 *     komponent má vlastní `toast.error(e.message)` v mutation onError).
 *     Regres by byl dvojitý toast, chceme co nejmenší invazi.
 *   - Volání ≥ 800ms → loading toast s labelem ("Mažu GitLab instanci…"),
 *     po completion updatován na success/error. Loading toast už zaručuje,
 *     že user vidí co se děje — success/error update ho jen ukončí správným
 *     stavem, žádná duplikace s komponent-level toastem se nedá vyhnout,
 *     ale je to jen pro DLOUHÁ volání (edge case).
 *   - Silent mode → žádný toast (background polling, side-effect calls).
 *
 * MCP tool response `{ok:false}` je považován za failure i když network
 * volání prošlo (200 OK). `error` field z response se použije jako text
 * toastu; fallback = label.
 *
 * Používá se globálně přes `api.mcp()` / `lib/mcp.ts::mcpCall` — žádná
 * komponenta nemusí implementovat vlastní loading UX pro delete/deploy/etc.
 */
import { toast } from 'sonner';
import i18n from './i18n';

const LOADING_THRESHOLD_MS = 800;
const SUCCESS_TOAST_DURATION_MS = 2500;

export interface OperationFeedbackOptions {
  /** Loading label. Fallback = toolName (např. "gitlab_delete"). */
  label?: string;
  /** true = žádný toast (background polling, silent utils). Default false. */
  silent?: boolean;
}

interface MaybeToolResult {
  ok?: boolean;
  error?: string;
  locked?: boolean;
  backup_failed?: boolean;
}

/** Rozliší uživatelsky-viditelnou tool chybu (ok:false + error) od úspěchu. */
function isToolFailure(result: unknown): result is MaybeToolResult {
  return (
    typeof result === 'object' &&
    result !== null &&
    (result as MaybeToolResult).ok === false
  );
}

function errorMessage(result: MaybeToolResult, fallback: string): string {
  const err = result.error as unknown;
  if (err == null) return fallback;
  if (typeof err === 'string') return err;
  if (typeof err === 'object' && err !== null && 'message' in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === 'string' && m) return m;
  }
  try {
    return JSON.stringify(err);
  } catch {
    return fallback;
  }
}

export async function withOperationFeedback<T>(
  fn: () => Promise<T>,
  toolName: string,
  opts: OperationFeedbackOptions = {},
): Promise<T> {
  if (opts.silent) return fn();

  const label = opts.label ?? toolName;
  let toastId: string | number | undefined;

  const timer = window.setTimeout(() => {
    toastId = toast.loading(label);
  }, LOADING_THRESHOLD_MS);

  try {
    const result = await fn();
    window.clearTimeout(timer);

    if (toastId !== undefined) {
      // Loading toast byl zobrazený → nahradit success/error updatem
      if (isToolFailure(result)) {
        toast.error(
          errorMessage(result, i18n.t('common.operationFailed', '{{label}}: operace neúspěšná', { label })),
          { id: toastId },
        );
      } else {
        toast.success(label, { id: toastId, duration: SUCCESS_TOAST_DURATION_MS });
      }
    }
    // Krátká operace → žádný platform toast (komponenta si to řeší)

    return result;
  } catch (e) {
    window.clearTimeout(timer);
    if (toastId !== undefined) {
      // Loading toast byl zobrazený → nahradit error updatem
      const msg = e instanceof Error ? e.message : i18n.t('common.unknownError', 'neznámá chyba');
      toast.error(`${label}: ${msg}`, { id: toastId });
    }
    // Krátká chyba → komponent-level onError toast, žádný wrapper toast
    throw e;
  }
}
